/**
 * CommittedMoney (revised) — segmented category bar + list + amber note
 * + "Review committed money →" that opens a drawer via onOpenDrawer.
 */
import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatMoney } from '@/lib/format';

/** Fallback categories from raw commitments if no explicit list supplied. */
function categoriesFromItems(items = []) {
  const out = {};
  const PALETTE = ['hsl(var(--primary))', 'hsl(var(--emerald))', 'hsl(var(--sage))', 'hsl(var(--gold))', 'hsl(var(--muted-warm))'];
  const LABEL = { expense: 'Expenses', investment: 'Investments', debt_payment: 'Debt', income: 'Income' };
  for (const c of items) {
    if (c.active === false) continue;
    if (c.kind === 'income') continue;
    const key = c.kind || 'other';
    if (!out[key]) out[key] = { key, label: LABEL[key] || key, amount_base: 0 };
    out[key].amount_base += (c.monthly_equivalent_base || 0);
  }
  const rows = Object.values(out).sort((a, b) => b.amount_base - a.amount_base);
  return rows.map((r, i) => ({ ...r, color: PALETTE[i % PALETTE.length] }));
}

export default function CommittedMoney({
  totalMonthlyOutBase = null,
  activeCount = null,
  pctOfIncome = null,
  categories = null,       // optional — supplied by parent (demo mode / future backend)
  items = [],              // raw snapshot.commitments.items — used as fallback
  reviewNote = null,       // optional amber-bulleted note
  loading = false,
  baseCurrency = 'INR',
  onOpenDrawer,
}) {
  const cats = useMemo(() => {
    if (Array.isArray(categories) && categories.length > 0) return categories;
    return categoriesFromItems(items);
  }, [categories, items]);

  if (loading) {
    return (
      <Card data-testid="dashboard-committed-money" className="border-border/70 h-full">
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-3 w-full mt-3" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const empty = !totalMonthlyOutBase && (activeCount || 0) === 0 && cats.length === 0;
  const total = totalMonthlyOutBase || cats.reduce((s, c) => s + (c.amount_base || 0), 0);

  return (
    <Card data-testid="dashboard-committed-money" className="border-border/70 h-full">
      <CardContent className="p-6">
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">Committed Money</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Money already spoken for before everyday spending begins.</p>
        </div>

        {empty ? (
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">You haven&apos;t set up any recurring commitments yet.</p>
            <Button asChild variant="ghost" className="px-0 mt-2 text-primary hover:bg-transparent">
              <Link to="/recurring" data-testid="committed-add-link">Add recurring <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-4">
              <div className="num text-3xl font-display font-semibold text-foreground" data-testid="committed-total">
                {formatMoney(total, baseCurrency)}<span className="text-base text-muted-foreground font-normal">/month</span>
              </div>
              {pctOfIncome != null && (
                <p className="text-sm text-muted-foreground mt-1" data-testid="committed-pct-of-income">
                  {Math.round(pctOfIncome)}% of your known monthly income is committed to recurring expenses.
                </p>
              )}
            </div>

            {/* Segmented proportion bar */}
            <div className="mt-4 h-2.5 rounded-full overflow-hidden flex bg-muted" data-testid="committed-segments" role="img" aria-label="Category breakdown">
              {cats.map((c) => {
                const pct = total > 0 ? (c.amount_base / total) * 100 : 0;
                return (
                  <div key={c.key} style={{ width: `${pct}%`, backgroundColor: c.color }} title={`${c.label}: ${formatMoney(c.amount_base, baseCurrency)}`} />
                );
              })}
            </div>

            {/* Category list */}
            <ul className="mt-4 space-y-2.5" data-testid="committed-category-list">
              {cats.map((c) => (
                <li key={c.key} className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2.5 text-sm text-foreground">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                    {c.label}
                  </span>
                  <span className="num text-sm text-foreground">{formatMoney(c.amount_base, baseCurrency)}</span>
                </li>
              ))}
            </ul>

            {/* Amber-bulleted note */}
            {reviewNote && (
              <div className="mt-4 pt-4 border-t border-border/50 flex gap-2" data-testid="committed-note">
                <span className="h-1.5 w-1.5 rounded-full bg-gold mt-2 shrink-0" />
                <p className="text-xs text-muted-foreground">{reviewNote}</p>
              </div>
            )}

            <Button
              variant="ghost"
              className="px-0 mt-3 text-primary hover:bg-transparent hover:text-primary"
              onClick={() => onOpenDrawer?.('committed')}
              data-testid="committed-review-link"
            >
              Review committed money <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
